import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { RiSearchLine, RiCloseFill, RiMoneyDollarCircleLine } from "react-icons/ri";
import { FiEdit2, FiUsers, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { MdPublishedWithChanges, MdOutlineLock, MdOutlineLockOpen } from "react-icons/md";
import { Menu, MenuItem } from "@mui/material";

import api from "../../services/api";
import { useAuth } from "../../hooks/AuthContext";
import { roles, customerSituations } from "../../utils/constants";
import {
  formatDocument,
  formatPhone,
  maskCpfCnpj,
  getLicenseeDisplayName,
} from "../../utils/formatters";
import { showToast } from "../../components/CustomToast";
import { Table, Column, TableAction } from "../../components/Table";
import { DropdownFilter } from "../../components/DropdownFilter";
import { Modal } from "../../components/Modal";
import { FormModal } from "../../components/FormModal";
import { FloatingSelect } from "../../components/FloatingSelect";
import { NewLicenseeModal } from "../../components/NewLicenseeModal";
import { LicenseeProvisionerList } from "../Licensee/components/LicenseeProvisionerList";
import { AddFixcoinModal } from "../Licensee/components/AddFixcoinModal";
import { CustomerLocationPin } from "./components/CustomerLocationPin";
import { CustomerPlanBadge, plansMapCache } from "./components/CustomerPlanBadge";
import { Loading } from "../../components/Loading";
import { i18n } from "../../localization/i18n";
import NoInfo from "../../components/NoInfo";

import { CustomerType, Licensee, ISearchSuggestion, IPlans } from "./dtos";
import {
  Container,
  HeaderActions,
  SearchContainer,
  SearchSelectWrapper,
  SearchSelectButton,
  SearchInput,
  SearchButton,
  SearchSuggestionsList,
  SearchSuggestionItem,
  SelectedFilterBadge,
  SelectedFilterTag,
  SelectedFilterText,
  SelectedFilterRemoveButton,
  CustomerInfoCell,
  CustomerName,
  DocumentText,
  PhoneText,
  StatusBolinha,
  StatusCell,
  EmptyContainer,
  EmptyText,
  DescriptionHeaderWrapper,
  DescriptionHeaderText,
  HeaderCountBadge,
  SortableHeader,
} from "./styles";

interface CustomersListProps {
  customerType: CustomerType;
}

type HealthScoreCategory = "active" | "cooling" | "risk" | "no_orders";

const getCustomerHealthScore = (
  customer: Licensee
): {
  category: HealthScoreCategory;
  label: string;
  days: number | null;
} => {
  const rawDays = customer.days_since_last_order;
  if (rawDays === null || rawDays === undefined || Number.isNaN(Number(rawDays))) {
    return {
      category: "no_orders",
      label: "Sem pedidos",
      days: null,
    };
  }

  const days = Math.max(0, Math.floor(Number(rawDays)));
  const label =
    days === 0 ? "Hoje" : days === 1 ? "Há 1 dia" : `Há ${days} dias`;

  if (days <= 30) {
    return { category: "active", label, days };
  }
  if (days <= 60) {
    return { category: "cooling", label, days };
  }
  return { category: "risk", label, days };
};

export const isCustomerBlocked = (customer: Licensee): boolean => {
  if (customer.signature_status === "blocked") return true;
  if (customer.is_blocked === true) return true;
  if (customer.situation === "blocked") return true;

  const rawSig = customer.signature || (customer as any).signatures;
  const sigs: any[] = Array.isArray(rawSig) ? rawSig : rawSig ? [rawSig] : [];

  return sigs.some(
    (s: any) =>
      Boolean(s) &&
      (!s.end_date || new Date(s.end_date).getTime() >= Date.now()) &&
      (s.is_blocked === true || s.status === "blocked")
  );
};

export const isCustomerCancelled = (customer: Licensee): boolean => {
  // Clientes com bloqueio administrativo NUNCA são considerados cancelados
  if (isCustomerBlocked(customer)) {
    return false;
  }

  if (customer.signature_status === "cancelled") {
    return true;
  }
  if (customer.signature_status === "active") {
    return false;
  }

  const rawSig = customer.signature || (customer as any).signatures;
  const sigs: any[] = Array.isArray(rawSig) ? rawSig : rawSig ? [rawSig] : [];

  // Se tem ao menos uma assinatura vigente (sem end_date ou com end_date futuro, e não cancelada)
  const hasOpenSignature = sigs.some(
    (s: any) =>
      Boolean(s) &&
      (!s.end_date || new Date(s.end_date).getTime() >= Date.now()) &&
      s.status !== "cancelled" &&
      s.status !== "canceled"
  );

  if (hasOpenSignature) {
    return false;
  }

  // Se não tem assinatura vigente, verifica se possui assinatura expirada (end_date < NOW()) ou status cancelado
  const hasEndedOrCancelledSignature = sigs.some(
    (s: any) =>
      Boolean(s) &&
      (s.status === "cancelled" ||
        s.status === "canceled" ||
        (Boolean(s.end_date) && new Date(s.end_date).getTime() < Date.now()))
  );

  return Boolean(
    (customer as any).cancelled_at ||
    (customer as any).churned_at ||
    (customer as any).situation === "cancelled" ||
    (customer as any).status === "cancelled" ||
    (Boolean((customer as any).end_date) &&
      new Date((customer as any).end_date).getTime() < Date.now()) ||
    hasEndedOrCancelledSignature
  );
};

export const getCustomerStatus = (customer: Licensee) => {
  if (isCustomerBlocked(customer)) {
    return {
      label: "Bloqueado",
      color: "var(--error)",
    };
  }
  if (isCustomerCancelled(customer)) {
    return {
      label: "Cancelado",
      color: "#6c757d",
    };
  }
  if (customer.situation === customerSituations.onboarding) {
    return {
      label: "Onboarding",
      color: "var(--development)",
    };
  }
  return {
    label: "Operacional",
    color: "var(--active-green)",
  };
};

export function CustomersList({ customerType }: CustomersListProps) {
  const { setNavigationAction, user } = useAuth();
  const navigate = useNavigate();

  const isFixiter = customerType === "fixiters";
  const pageTitle = isFixiter ? "Fixiters" : "Licenciados";

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Licensee[]>([]);
  const [totalCustomers, setTotalCustomers] = useState(0);

  // Search states
  const [searchType, setSearchType] = useState<"name" | "document">("document");
  const [searchMenuAnchorEl, setSearchMenuAnchorEl] = useState<null | HTMLElement>(null);
  const openSearchMenu = Boolean(searchMenuAnchorEl);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<ISearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<ISearchSuggestion | null>(null);

  // Filter & Sort states
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [availablePlans, setAvailablePlans] = useState<IPlans[]>([]);
  const [lastOrderSort, setLastOrderSort] = useState<"desc" | "asc" | null>(null);

  useEffect(() => {
    let isMounted = true;
    api
      .get("/plans", {
        params: {
          has_printer: !isFixiter,
          is_active: true,
        },
      })
      .then((res) => {
        if (isMounted && Array.isArray(res.data)) {
          setAvailablePlans(res.data);
          res.data.forEach((p: any) => {
            if (p.id && p.plan_name) {
              plansMapCache.set(String(p.id), p.plan_name);
            }
          });
        }
      })
      .catch(() => {
        // Fallback
      });
    return () => {
      isMounted = false;
    };
  }, [isFixiter]);

  // Modals and selected entity states
  const [selectedCustomer, setSelectedCustomer] = useState<Licensee | null>(null);
  const [newLicenseeModal, setNewLicenseeModal] = useState(false);
  const [openProvisionerModal, setOpenProvisionerModal] = useState(false);
  const [openAddFixcoinModal, setOpenAddFixcoinModal] = useState(false);
  const [customerForFixcoin, setCustomerForFixcoin] = useState<Licensee | null>(null);

  // Status modal
  const [editingStatusCustomer, setEditingStatusCustomer] = useState<Licensee | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>(customerSituations.active);
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  // Main data fetch
  const getCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const queryList: string[] = [];

      // Separation rule: Fixiters vs Licenciados (Plan has_printer rule)
      if (isFixiter) {
        queryList.push("has_printer=false");
      } else {
        queryList.push("has_printer=true");
      }

      // Filter: ID from suggestion
      if (selectedSuggestion?.id) {
        queryList.push(`licensee_id=${selectedSuggestion.id}`);
      }

      // Filter: Status (Operacional / Onboarding / Bloqueado / Cancelado)
      const selectedStatuses = appliedFilters.status || [];
      const hasCancelled = selectedStatuses.includes("cancelled");
      const hasBlocked = selectedStatuses.includes("blocked");
      const hasOperational = selectedStatuses.includes("operational");
      const hasOnboarding = selectedStatuses.includes("onboarding");

      // Filter: Plan ID
      if (appliedFilters.plan_id && appliedFilters.plan_id.length > 0) {
        queryList.push(`plan_id=${appliedFilters.plan_id[0]}`);
      }

      // Filter: Tipo de Conta (Padrão / Teste)
      const selectedAccountTypes = appliedFilters.account_type || [];
      const hasStandardAccount = selectedAccountTypes.includes("standard");
      const hasTestAccount = selectedAccountTypes.includes("test");

      if (hasStandardAccount && hasTestAccount) {
        queryList.push("is_test=all");
      } else if (hasTestAccount) {
        queryList.push("is_test=true");
      } else if (hasStandardAccount) {
        queryList.push("is_test=false");
      }

      let data: Licensee[] = [];

      if (hasCancelled && (hasBlocked || hasOperational || hasOnboarding)) {
        // Usuário escolheu Cancelados E outro(s) status ativos:
        // Busca ativa + busca de cancelados em paralelo para unir a lista
        const baseQuery = queryList.length > 0 ? `?${queryList.join("&")}` : "";
        const cancelQuery = queryList.length > 0
          ? `?${queryList.join("&")}&signature_status=cancelled`
          : `?signature_status=cancelled`;

        const [activeRes, cancelRes] = await Promise.all([
          api.get(`/licensees${baseQuery}`),
          api.get(`/licensees${cancelQuery}`),
        ]);

        const combinedMap = new Map<string, Licensee>();
        [...(activeRes.data?.data || []), ...(cancelRes.data?.data || [])].forEach((item) => {
          if (item && item.id) combinedMap.set(item.id, item);
        });
        data = Array.from(combinedMap.values());
      } else if (hasCancelled && !hasBlocked && !hasOperational && !hasOnboarding) {
        // Apenas cancelados selecionados
        queryList.push("signature_status=cancelled");
        const queryString = queryList.length > 0 ? `?${queryList.join("&")}` : "";
        const response = await api.get(`/licensees${queryString}`);
        data = response.data?.data || [];
      } else {
        // Cancelado NÃO selecionado (inclusive filtro vazio por padrão)
        if (hasBlocked && !hasOperational && !hasOnboarding) {
          queryList.push("is_blocked=true");
          queryList.push("signature_status=blocked");
        } else if (!hasBlocked && (hasOperational || hasOnboarding)) {
          queryList.push("is_blocked=false");
          queryList.push("signature_status=active");
          if (hasOnboarding && !hasOperational) {
            queryList.push("situation=onboarding");
          }
        }
        const queryString = queryList.length > 0 ? `?${queryList.join("&")}` : "";
        const response = await api.get(`/licensees${queryString}`);
        data = response.data?.data || [];
      }

      // In-memory filter: Tipo de Conta (Padrão vs Teste)
      data = data.filter((item) => {
        const itemIsTest = Boolean((item as any).is_test);
        if (hasStandardAccount && hasTestAccount) return true;
        if (hasTestAccount) return itemIsTest;
        return !itemIsTest;
      });

      // In-memory status filter:
      // REGRA OBRIGATÓRIA: Clientes com assinatura cancelada NUNCA aparecem na listagem,
      // a menos que o usuário tenha explicitamente selecionado "Cancelado" no filtro.
      if (selectedStatuses.length > 0) {
        data = data.filter((item) => {
          if (isCustomerBlocked(item)) {
            return hasBlocked;
          }

          if (isCustomerCancelled(item)) {
            return hasCancelled;
          }

          if (item.situation === customerSituations.onboarding) {
            return hasOnboarding;
          }

          return hasOperational;
        });
      } else {
        // Sem seleção no filtro (padrão): exibe carteira ativa (operacional, onboarding e bloqueados) e remove cancelados
        data = data.filter((item) => !isCustomerCancelled(item));
      }

      // In-memory filter: Health Score (apenas para Licenciados)
      const selectedHealthScores = !isFixiter ? appliedFilters.health_score || [] : [];
      if (selectedHealthScores.length > 0) {
        data = data.filter((item) => {
          const { category } = getCustomerHealthScore(item);
          return selectedHealthScores.includes(category);
        });
      }

      setCustomers(data);
      setTotalCustomers(data.length);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      showToast({ type: "error", message: `Erro ao buscar ${pageTitle.toLowerCase()}.` });
    } finally {
      setLoading(false);
    }
  }, [isFixiter, selectedSuggestion, appliedFilters, pageTitle]);

  const filteredCustomers = useMemo(() => {
    let result = customers;

    if (searchQuery.trim() && !selectedSuggestion) {
      if (searchType === "document") {
        const docDigits = searchQuery.replace(/\D/g, "");
        if (docDigits) {
          result = customers.filter((item) =>
            (item.document_number || "").replace(/\D/g, "").includes(docDigits)
          );
        }
      } else {
        const query = searchQuery.toLowerCase().trim();
        const queryDigits = query.replace(/\D/g, "");
        result = customers.filter(
          (item) =>
            item.name?.toLowerCase().includes(query) ||
            item.company_name?.toLowerCase().includes(query) ||

            (queryDigits &&
              item.document_number?.replace(/\D/g, "").includes(queryDigits)) ||
            item.manager_name?.toLowerCase().includes(query) ||
            item.city?.toLowerCase().includes(query) ||
            item.state?.toLowerCase().includes(query)
        );
      }
    }

    if (!isFixiter && lastOrderSort) {
      result = [...result].sort((a, b) => {
        const daysA =
          a.days_since_last_order !== null &&
          a.days_since_last_order !== undefined &&
          !Number.isNaN(Number(a.days_since_last_order))
            ? Number(a.days_since_last_order)
            : null;
        const daysB =
          b.days_since_last_order !== null &&
          b.days_since_last_order !== undefined &&
          !Number.isNaN(Number(b.days_since_last_order))
            ? Number(b.days_since_last_order)
            : null;

        // "Sem pedidos" (null) sempre no final da lista
        if (daysA === null && daysB === null) return 0;
        if (daysA === null) return 1;
        if (daysB === null) return -1;

        return lastOrderSort === "desc" ? daysB - daysA : daysA - daysB;
      });
    }

    return result;
  }, [customers, searchQuery, searchType, selectedSuggestion, isFixiter, lastOrderSort]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  // Autocomplete search suggestions (debounced)
  useEffect(() => {
    const rawDigits = searchTerm.replace(/\D/g, "");
    const term = searchType === "document" ? rawDigits : searchTerm.trim();

    if (term.length < 3 || selectedSuggestion) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/licensees/search?term=${encodeURIComponent(term)}`);
        let fetched: ISearchSuggestion[] = data || [];

        // If searching by document, also augment with local matches in loaded customers
        if (searchType === "document") {
          const localMatches: ISearchSuggestion[] = customers
            .filter((c) => (c.document_number || "").replace(/\D/g, "").includes(rawDigits))
            .map((c) => ({
              id: c.id,
              name: c.name,
              company_name: c.company_name,

              person_type: c.person_type,
              document_number: c.document_number,
            }));

          const combined = [...fetched, ...localMatches];
          const seen = new Set<string>();
          fetched = combined.filter((item) => {
            if (!item.id || seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          });
        }

        setSuggestions(fetched.slice(0, 5));
        setShowSuggestions(fetched.length > 0);
      } catch (err) {
        console.error("Erro ao buscar sugestões:", err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchType, selectedSuggestion, customers]);

  // Search handlers
  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setShowSuggestions(false);
      setSearchQuery(searchTerm.trim());
    },
    [searchTerm]
  );

  const handleSearchTypeChange = useCallback(
    (type: "name" | "document") => {
      setSearchType(type);
      setSelectedSuggestion(null);
      setSearchTerm("");
      setSearchQuery("");
      setSuggestions([]);
      setShowSuggestions(false);
    },
    []
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (searchType === "document") {
        setSearchTerm(maskCpfCnpj(e.target.value));
      } else {
        setSearchTerm(e.target.value);
      }
    },
    [searchType]
  );

  const handleSelectSuggestion = useCallback(
    (suggestion: ISearchSuggestion) => {
      setSelectedSuggestion(suggestion);
      const displayValue =
        searchType === "document" && suggestion.document_number
          ? formatDocument(suggestion.document_number)
          : getLicenseeDisplayName(suggestion);
      setSearchTerm(displayValue);
      setSearchQuery(displayValue);
      setShowSuggestions(false);
    },
    [searchType]
  );

  const handleClearSearch = useCallback(() => {
    setSelectedSuggestion(null);
    setSearchTerm("");
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  // Filter apply
  const handleFilterApply = (selections: Record<string, string[]>) => {
    setAppliedFilters(selections);
  };

  // Action: Block / Unblock Customer
  const handleToggleBlock = useCallback(
    async (customer: Licensee) => {
      const blocked = isCustomerBlocked(customer);
      const actionLabel = blocked ? "desbloquear" : "bloquear";
      const customerDisplayName = getLicenseeDisplayName(customer);
      if (
        window.confirm(
          `Deseja ${actionLabel} ${isFixiter ? "o fixiter" : "o licenciado"} ${customerDisplayName}?`
        )
      ) {
        try {
          await api.put(`/licensees/${customer.id}/block?block=${!blocked}`);
          showToast({
            type: "success",
            message: blocked
              ? `${customerDisplayName} ativado com sucesso.`
              : `${customerDisplayName} bloqueado com sucesso.`,
          });
          getCustomers();
        } catch {
          showToast({
            type: "error",
            message: `Erro ao ${actionLabel} ${customerDisplayName}.`,
          });
        }
      }
    },
    [isFixiter, getCustomers]
  );



  // Action: Change Status
  const handleChangeStatus = async () => {
    if (!editingStatusCustomer) return;
    setIsChangingStatus(true);
    try {
      await api.patch(`/licensees/${editingStatusCustomer.id}/situation?situation=${selectedStatus}`);
      showToast({ type: "success", message: "Status alterado com sucesso!" });
      setEditingStatusCustomer(null);
      getCustomers();
    } catch {
      showToast({ type: "error", message: "Erro ao modificar status." });
    } finally {
      setIsChangingStatus(false);
    }
  };


  // Navigation action setup (Header Bar)
  useEffect(() => {
    setNavigationAction({
      action: "back",
      hasButton: false,
      description: (
        <DescriptionHeaderWrapper>
          <DescriptionHeaderText>{pageTitle}</DescriptionHeaderText>
          <HeaderCountBadge>{loading ? "..." : filteredCustomers.length}</HeaderCountBadge>
        </DescriptionHeaderWrapper>
      ) as any,
      customElement: (
        <HeaderActions>
          <SearchContainer onSubmit={handleSearch}>
            <SearchSelectWrapper>
              <SearchSelectButton
                id="search-select-button"
                aria-controls={openSearchMenu ? "search-select-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={openSearchMenu ? "true" : undefined}
                onClick={(e) => setSearchMenuAnchorEl(e.currentTarget)}
                type="button"
              >
                {searchType === "name" ? "Nome" : "Documento"}
                {openSearchMenu ? <FiChevronUp /> : <FiChevronDown />}
              </SearchSelectButton>
              <Menu
                id="search-select-menu"
                anchorEl={searchMenuAnchorEl}
                open={openSearchMenu}
                onClose={() => setSearchMenuAnchorEl(null)}
                MenuListProps={{
                  "aria-labelledby": "search-select-button",
                }}
                PaperProps={{
                  sx: {
                    borderRadius: "12px",
                    marginTop: "4px",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleSearchTypeChange("document");
                    setSearchMenuAnchorEl(null);
                  }}
                  sx={{
                    fontSize: "14px",
                    fontWeight: searchType === "document" ? 600 : 500,
                    color: searchType === "document" ? "var(--fixit)" : "var(--text)",
                    padding: "8px 24px",
                  }}
                >
                  Documento
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleSearchTypeChange("name");
                    setSearchMenuAnchorEl(null);
                  }}
                  sx={{
                    fontSize: "14px",
                    fontWeight: searchType === "name" ? 600 : 500,
                    color: searchType === "name" ? "var(--fixit)" : "var(--text)",
                    padding: "8px 24px",
                  }}
                >
                  Nome
                </MenuItem>
              </Menu>
            </SearchSelectWrapper>

            {searchQuery ? (
              <SelectedFilterBadge>
                <SelectedFilterTag>
                  <SelectedFilterRemoveButton type="button" onClick={handleClearSearch}>
                    <RiCloseFill />
                  </SelectedFilterRemoveButton>
                  <SelectedFilterText>
                    {selectedSuggestion
                      ? searchType === "document" && selectedSuggestion.document_number
                        ? formatDocument(selectedSuggestion.document_number)
                        : getLicenseeDisplayName(selectedSuggestion)
                      : searchType === "document"
                      ? maskCpfCnpj(searchQuery)
                      : searchQuery}
                  </SelectedFilterText>
                </SelectedFilterTag>
              </SelectedFilterBadge>
            ) : (
              <SearchInput
                type="text"
                pattern={searchType === "document" ? "[0-9.-/]*" : undefined}
                inputMode={searchType === "document" ? "numeric" : "text"}
                placeholder={isFixiter ? "Procurar fixiter..." : "Procurar licenciado..."}
                value={searchTerm}
                onChange={handleSearchInputChange}
                $hasText={!!searchTerm.trim()}
              />
            )}
            {showSuggestions && suggestions.length > 0 && (
              <SearchSuggestionsList>
                {suggestions.map((suggestion) => (
                  <SearchSuggestionItem
                    key={suggestion.id}
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    {searchType === "document" && suggestion.document_number
                      ? `${formatDocument(suggestion.document_number)} - ${getLicenseeDisplayName(suggestion)}`
                      : `${getLicenseeDisplayName(suggestion)}${
                          suggestion.document_number
                            ? ` - ${formatDocument(suggestion.document_number)}`
                            : ""
                        }`}
                  </SearchSuggestionItem>
                ))}
              </SearchSuggestionsList>
            )}
            <SearchButton type="submit">
              <RiSearchLine />
            </SearchButton>
          </SearchContainer>

          <DropdownFilter
            multiple={true}
            layout="orders"
            groups={[
              {
                id: "status",
                title: "Status",
                options: [
                  { label: "Operacional", value: "operational" },
                  { label: "Onboarding", value: "onboarding" },
                  { label: "Bloqueado", value: "blocked" },
                  { label: "Cancelado", value: "cancelled" },
                ],
              },
              ...(!isFixiter
                ? [
                    {
                      id: "health_score",
                      title: "Health Score",
                      options: [
                        { label: "Ativo (≤ 30 dias)", value: "active" },
                        { label: "Esfriando (31 a 60 dias)", value: "cooling" },
                        { label: "Risco (> 60 dias)", value: "risk" },
                        { label: "Sem pedidos", value: "no_orders" },
                      ],
                    },
                  ]
                : []),
              {
                id: "plan_id",
                title: "Plano",
                options: availablePlans.map((p) => ({
                  label: p.plan_name,
                  value: String(p.id),
                })),
              },
              {
                id: "account_type",
                title: "Tipo de Conta",
                options: [
                  { label: "Padrão", value: "standard" },
                  { label: "Teste", value: "test" },
                ],
              },
            ]}
            defaultValues={{
              status: appliedFilters.status || [],
              ...(!isFixiter ? { health_score: appliedFilters.health_score || [] } : {}),
              plan_id: appliedFilters.plan_id || [],
              account_type: appliedFilters.account_type || [],
            }}
            onApply={handleFilterApply}
          />
        </HeaderActions>
      ),
    });
  }, [
    setNavigationAction,
    pageTitle,
    totalCustomers,
    filteredCustomers.length,
    loading,
    isFixiter,
    searchType,
    searchTerm,
    searchQuery,
    searchMenuAnchorEl,
    openSearchMenu,
    selectedSuggestion,
    showSuggestions,
    suggestions,
    appliedFilters,
    availablePlans,
    handleSearch,
    handleSearchTypeChange,
    handleSearchInputChange,
    handleSelectSuggestion,
    handleClearSearch,
    user,
  ]);

  // Table Columns
  const columns: Column<Licensee>[] = useMemo(
    () => [
      {
        key: "identification",
        header: isFixiter ? "Fixiter" : "Licenciado",
        width: isFixiter ? "30%" : "24%",
        render: (row) => {
          const { color: statusColor } = getCustomerStatus(row);
          return (
            <CustomerInfoCell>
              <CustomerName color={statusColor}>{getLicenseeDisplayName(row, "-")}</CustomerName>
              <CustomerLocationPin customer={row} />
            </CustomerInfoCell>
          );
        },
      },
      {
        key: "status",
        header: "Status",
        width: isFixiter ? "16%" : "12%",
        render: (row) => {
          const { label, color } = getCustomerStatus(row);
          return (
            <StatusCell color={color}>
              <StatusBolinha color={color} />
              {label}
            </StatusCell>
          );
        },
      },
      {
        key: "plan",
        header: "Plano",
        width: isFixiter ? "16%" : "12%",
        render: (row) => <CustomerPlanBadge customer={row} />,
      },
      ...(!isFixiter
        ? ([
            {
              key: "last_order",
              header: "Último pedido",
              width: "13%",
              onHeaderClick: () => {
                setLastOrderSort((prev) =>
                  prev === null ? "desc" : prev === "desc" ? "asc" : null
                );
              },
              headerRender: () => (
                <SortableHeader $active={Boolean(lastOrderSort)}>
                  Último pedido
                  {lastOrderSort === "asc" ? (
                    <FiChevronUp />
                  ) : (
                    <FiChevronDown
                      style={{ opacity: lastOrderSort === "desc" ? 1 : 0.45 }}
                    />
                  )}
                </SortableHeader>
              ),
              render: (row: Licensee) => {
                const { label } = getCustomerHealthScore(row);
                const formattedDate = row.last_order_date
                  ? new Date(row.last_order_date).toLocaleDateString("pt-BR")
                  : undefined;
                return (
                  <DocumentText
                    title={
                      formattedDate ? `Último pedido: ${formattedDate}` : undefined
                    }
                  >
                    {label}
                  </DocumentText>
                );
              },
            },
          ] as Column<Licensee>[])
        : []),
      {
        key: "document_number",
        header: "Documento",
        width: isFixiter ? "19%" : "18%",
        render: (row) => <DocumentText>{formatDocument(row.document_number)}</DocumentText>,
      },
      {
        key: "phone",
        header: "Telefone",
        width: isFixiter ? "19%" : "16%",
        render: (row) => <PhoneText>{formatPhone(row.phone)}</PhoneText>,
      },
    ],
    [isFixiter, lastOrderSort]
  );

  // Table Actions
  const actions: TableAction<Licensee>[] = useMemo(
    () => [
      {
        label: "Editar",
        icon: <FiEdit2 size={16} />,
        onClick: (row) => {
          navigate(`/customers/edit/${row.id}`, {
            state: { customerType: isFixiter ? "fixiters" : "licensees" },
          });
        },
      },
      {
        label: "Alterar Status",
        icon: <MdPublishedWithChanges size={16} />,
        hidden: (row) => isCustomerCancelled(row),
        onClick: (row) => {
          setEditingStatusCustomer(row);
          setSelectedStatus(row.situation || customerSituations.active);
        },
      },
      {
        label: "Adicionar FixCoins",
        icon: <RiMoneyDollarCircleLine size={16} />,
        hidden: (row) => {
          if (isFixiter || isCustomerCancelled(row)) return true;
          const rawSigs = Array.isArray(row.signature) ? row.signature : row.signature ? [row.signature] : [];
          const sigs = [...rawSigs].sort((a: any, b: any) => {
            const timeA = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b?.created_at ? new Date(b.created_at).getTime() : 0;
            return timeB - timeA;
          });
          const activeSig =
            (row as any).active_signature ||
            sigs.find(
              (s: any) =>
                Boolean(s) &&
                (!s.end_date || new Date(s.end_date).getTime() >= Date.now()) &&
                s.status !== "cancelled" &&
                s.status !== "canceled"
            ) ||
            sigs.find((s: any) => s.is_activated) ||
            sigs[0];
          const planObj = activeSig?.plan || (activeSig as any)?.plans;
          return Boolean(planObj && planObj.has_printer === false);
        },
        onClick: (row) => {
          setCustomerForFixcoin(row);
          setOpenAddFixcoinModal(true);
        },
      },
      {
        label: (row) => (isCustomerBlocked(row) ? "Desbloquear" : "Bloquear"),
        icon: (row) =>
          isCustomerBlocked(row) ? (
            <MdOutlineLockOpen size={16} />
          ) : (
            <MdOutlineLock size={16} />
          ),
        color: (row) => (isCustomerBlocked(row) ? "var(--active-green)" : "var(--error)"),
        hidden: (row) => isCustomerCancelled(row),
        onClick: (row) => {
          handleToggleBlock(row);
        },
      },
    ],
    [navigate, isFixiter, handleToggleBlock]
  );

  return (
    <Container>
      {loading ? (
        <Loading
          loading={true}
          text={`${i18n.t("login.starting")}`}
          numberOfDots={3}
          dotSymbol="."
        />
      ) : filteredCustomers.length === 0 ? (
        <NoInfo
          noInfo={`Nenhum ${isFixiter ? "fixiter" : "licenciado"} encontrado com os filtros aplicados.`}
        />
      ) : (
        <Table
          columns={columns}
          data={filteredCustomers}
          actions={actions}
          onRowClick={(row) => {
            navigate(`/customers/edit/${row.id}`, {
              state: { customerType: isFixiter ? "fixiters" : "licensees" },
            });
          }}
        />
      )}

      {/* Alterar Status Modal */}
      {!!editingStatusCustomer && (
        <FormModal
          open={true}
          onRequestClose={() => !isChangingStatus && setEditingStatusCustomer(null)}
          title="Alterar Status"
          subtitle={`Escolha o status para ${getLicenseeDisplayName(editingStatusCustomer)}`}
          width={450}
          onSave={handleChangeStatus}
          isSaveLoading={isChangingStatus}
          saveText="Salvar Alterações"
          cancelText="Cancelar"
        >
          <div style={{ padding: "16px 0" }}>
            <FloatingSelect
              label="Status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value={customerSituations.onboarding}>Onboarding</option>
              <option value={customerSituations.active}>Ativo</option>
            </FloatingSelect>
          </div>
        </FormModal>
      )}

      {/* New Licensee / Customer Modal */}
      {newLicenseeModal && (
        <Modal
          open={newLicenseeModal}
          modalWidth={800}
          noPadding
          onRequestClose={() => setNewLicenseeModal(false)}
        >
          <NewLicenseeModal
            closeModal={() => setNewLicenseeModal(false)}
            getLicensees={getCustomers}
          />
        </Modal>
      )}

      {/* Add FixCoins Modal */}
      {openAddFixcoinModal && customerForFixcoin && (
        <AddFixcoinModal
          open={openAddFixcoinModal}
          licensee_id={customerForFixcoin.id}
          customerName={getLicenseeDisplayName(customerForFixcoin)}
          onHandleCloseRequest={() => {
            setOpenAddFixcoinModal(false);
            setCustomerForFixcoin(null);
          }}
          onSuccess={() => {
            setOpenAddFixcoinModal(false);
            setCustomerForFixcoin(null);
            getCustomers();
          }}
        />
      )}


      {/* FixPoint Selection for Fixiters without printers */}
      {openProvisionerModal && selectedCustomer && (
        <LicenseeProvisionerList
          handleProviderSelected={() => {
            getCustomers();
            setOpenProvisionerModal(false);
          }}
          trigger={openProvisionerModal}
          licenseeIdentification={selectedCustomer.id}
          onClose={() => {
            setOpenProvisionerModal(false);
            setSelectedCustomer(null);
          }}
          licensee={selectedCustomer}
        />
      )}
    </Container>
  );
}
export default CustomersList;
